import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AssetBadge,
  Button,
  Card,
  NetworkBadge,
  SendIcon,
  TextField,
} from '../components/ui';
import {
  HistoryFilters,
  filterByStatusAndPeriod,
  type PeriodFilter,
  type StatusFilter,
} from '../components/wallet/HistoryFilters';
import {
  TxHistoryRow,
  type TxStatus,
} from '../components/wallet/TxHistoryRow';
import {
  createWithdrawal,
  requestWithdrawalChallenge,
  useMyBalances,
  usePlatformConfig,
  useTransferQuote,
  useWithdrawals,
} from '../hooks/useApiQuery';
import { floorToCents } from '../lib/format';
import { tg } from '../lib/tg';
import type {
  BalancesChainBalance,
  WithdrawalItem,
  WithdrawalStatus,
} from '../lib/types';

const EVM_RE = /^0x[0-9a-fA-F]{40}$/;
const POLYGON_CHAIN_ID = 137;
const ARBITRUM_CHAIN_ID = 42161;

type Chain = 'polygon' | 'arbitrum';

// User-facing networks. Arbitrum is hidden in prod (HL bot disabled)
// but the type/constants stay so history rows on chain_id=42161 still
// render correctly through ``NetworkBadge``.
const VISIBLE_CHAINS: readonly Chain[] = ['polygon'] as const;

// ``CANONICAL_TOKEN`` is what the backend receives — always native USDC
// because bridged USDC.e is rejected by major CEX deposit addresses
// (Binance / OKX / Bybit / Coinbase). For `source='eoa'` the worker
// delivers native USDC by paying out the chain's native-USDC balance
// directly, then pre-swapping ONE non-canonical stable ({USDC.e, USDT})
// on that chain into native USDC via Uniswap V3 to close any shortfall;
// the pre-swap eats a 1% + 1-raw buffer (mirrors
// withdrawal_processor `eoa_path.PRE_SWAP_BUFFER_BPS=100`). No multi-leg
// aggregation. (The worker has a defensive CCTP-from-the-other-chain
// fallback, but the user-lane API's affordability check —
// `_read_eoa_withdraw_pool` — only sums the *selected* chain's stables,
// so a cross-chain row would be rejected at submit; we don't count it
// toward "available" here.)
const CANONICAL_TOKEN_BY_CHAIN: Record<Chain, string> = {
  polygon: 'USDC',
  arbitrum: 'USDC',
};
// Mirrors withdrawal_processor `eoa_path.PRE_SWAP_BUFFER_BPS` /
// `_MIN_PRE_SWAP_BUFFER_RAW` — see `arbUsdcRequiredForPm` /
// `maxPmFromArbUsdc` in Transfer.tsx for the same math on the allocate
// side. 6-decimal USDC.
const USDC_RAW = 1_000_000;
const PRE_SWAP_BUFFER_BPS = 100;
/** Max output you can get out of a `b`-sized swap source after the
 *  1% + 1-raw buffer the worker sizes inputs with. Inverse of
 *  `ceil(out·1.01)+1`. Clamps to 0 for dust. */
function maxAfterPreSwapBuffer(b: number): number {
  const raw = Math.round(b * USDC_RAW);
  return (
    Math.max(0, Math.floor(((raw - 1) * 10_000) / (10_000 + PRE_SWAP_BUFFER_BPS))) / USDC_RAW
  );
}

interface Props {
  embedded?: boolean;
}

export function Withdraw({ embedded = false }: Props = {}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const balances = useMyBalances();
  const history = useWithdrawals();

  const [chain, setChain] = useState<Chain>('polygon');
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState<'USDC' | 'pUSD'>('USDC');

  // Browser cabinet (no Telegram WebApp) confirms a withdrawal with a
  // one-time code the bot sends to the user's Telegram, instead of a
  // fresh ``initData``. ``codeSent`` flips the form into code-entry mode.
  const isBrowser = !tg.inTelegram;
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');

  // EOA pUSD (referral payouts) is withdrawable only on Polygon, only
  // when the platform flag is on, and only if the user holds some. The
  // worker unwraps pUSD→USDC before sending. ``effectiveToken`` collapses
  // to USDC whenever pUSD isn't eligible, so a stale 'pUSD' selection
  // (e.g. after switching chain) can't leak into the request.
  const platform = usePlatformConfig();
  const pusdBalance = floorToCents(tokenBalanceOnChain(balances.data, 'polygon', 'pUSD'));
  const pusdEligible =
    !!platform.data?.pusd_transfer_enabled && chain === 'polygon' && pusdBalance > 0;
  const effectiveToken: 'USDC' | 'pUSD' = pusdEligible && token === 'pUSD' ? 'pUSD' : 'USDC';

  // Clear the amount whenever the effective token flips — not only on a
  // manual toggle but also when pUSD stops being eligible (balance hits 0
  // after a withdraw, flag turns off, chain switch). Otherwise a pUSD-sized
  // amount could silently carry over into a USDC withdrawal.
  useEffect(() => {
    setAmount('');
    setError('');
  }, [effectiveToken]);

  // Max withdrawable on the selected chain, floored to cents. For pUSD
  // it's the raw on-chain pUSD (worker unwraps 1:1, no pre-swap). For
  // USDC it's NOT the 3-way stable sum — the worker delivers
  // `usdcNative[chain]` directly and then closes any shortfall with one
  // pre-swap of the best non-canonical stable on that chain ({USDC.e,
  // USDT}), haircut by the 1% buffer. Summing every stable
  // over-promised and the worker rejected the row with "insufficient
  // combined balance" at the eoa-aggregation planner. Flooring keeps
  // the displayed figure, the MAX button, and the submit guard in
  // lock-step (a raw 11.3164 must surface as 11.31, not 11.32, or MAX
  // fills an amount the guard rejects).
  // Live-quote sizing (transfer_live_quote_enabled): the withdrawal_processor
  // now pre-swaps USDC.e/USDT → native USDC at the real QuoterV2 rate
  // (#566), so the max is ~full balance, not balance/1.01. Only the USDC
  // Polygon path swaps; pUSD (1:1 unwrap) and any future chain keep the
  // client math. Falls back to the buffer until the quote resolves.
  const liveQuoteWithdraw =
    !!platform.data?.transfer_live_quote_enabled &&
    effectiveToken === 'USDC' &&
    chain === 'polygon';
  const quote = useTransferQuote('eoa_withdraw', POLYGON_CHAIN_ID, {
    enabled: liveQuoteWithdraw,
  });

  const available = useMemo(() => {
    if (effectiveToken === 'pUSD') return pusdBalance;
    if (liveQuoteWithdraw && quote.data) {
      return floorToCents(Number(quote.data.max_transferable_usdc));
    }
    return floorToCents(maxWithdrawableUsdc(balances.data, chain));
  }, [balances.data, chain, effectiveToken, pusdBalance, liveQuoteWithdraw, quote.data]);

  const amt = Number(amount || 0);
  const addrValid = EVM_RE.test(address);
  const amountValid = amt > 0 && amt <= available;
  const canSubmit = addrValid && amountValid;
  const codeValid = /^\d{6}$/.test(code);
  // swap-all = MAX under live quote: derived from "the amount still equals
  // the displayed quoted max", not a sticky flag — so a 10s quote refetch
  // that moved the max can't leave swap-all tagged on a stale amount (the
  // amount is frozen through the browser OTP window). Diverge → fixed-target.
  const maxSourceSwap = liveQuoteWithdraw && !!quote.data && amt === available;

  const tokenFor = (chainId: number): string =>
    effectiveToken === 'pUSD'
      ? 'pUSD'
      : CANONICAL_TOKEN_BY_CHAIN[
          chainId === POLYGON_CHAIN_ID ? 'polygon' : 'arbitrum'
        ];

  // Editing the address/amount after a code was sent invalidates it
  // (the code is bound to the exact params shown in the TG message), so
  // drop back to "request code" mode rather than submitting a stale code.
  const resetSentCode = () => {
    if (codeSent) {
      setCodeSent(false);
      setCode('');
    }
  };

  const mutation = useMutation({
    mutationFn: (req: {
      to_address: string;
      amount_usd: string;
      chain_id: number;
      stepup_code?: string;
      max_source_swap?: boolean;
    }) =>
      createWithdrawal({
        to_address: req.to_address,
        amount_usd: req.amount_usd,
        source: 'eoa',
        chain_id: req.chain_id,
        token: tokenFor(req.chain_id),
        stepup_code: req.stepup_code,
        max_source_swap: req.max_source_swap,
      }),
    onSuccess: async () => {
      tg.hapticSuccess();
      setAddress('');
      setAmount('');
      setError('');
      setCode('');
      setCodeSent(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['withdrawals'] }),
        qc.invalidateQueries({ queryKey: ['my-balances'] }),
      ]);
    },
    onError: () => {
      // Never surface raw server error text / details JSON to the user.
      setError(t('common.action_failed'));
    },
  });

  // Browser-only: request the bot to send a step-up code to Telegram.
  const challenge = useMutation({
    mutationFn: (req: { to_address: string; amount_usd: string; chain_id: number }) =>
      requestWithdrawalChallenge({
        to_address: req.to_address,
        amount_usd: req.amount_usd,
        source: 'eoa',
        chain_id: req.chain_id,
        token: tokenFor(req.chain_id),
      }),
    onSuccess: () => {
      tg.hapticSuccess();
      setError('');
      setCode('');
      setCodeSent(true);
    },
    onError: () => setError(t('common.action_failed')),
  });

  const validateAmountAddr = (): boolean => {
    setError('');
    if (!addrValid) {
      setError(t('withdraw.err_address'));
      return false;
    }
    if (!(amt > 0)) {
      setError(t('withdraw.err_amount'));
      return false;
    }
    if (amt > available) {
      setError(
        t('withdraw.err_insufficient', {
          needed: amt.toFixed(2),
          have: available.toFixed(2),
        }),
      );
      return false;
    }
    return true;
  };

  const chainId = chain === 'polygon' ? POLYGON_CHAIN_ID : ARBITRUM_CHAIN_ID;

  const submit = () => {
    if (!validateAmountAddr()) return;
    // Browser: first tap requests the code; second tap (with code
    // entered) submits. TG WebApp submits directly with initData.
    if (isBrowser && !codeSent) {
      challenge.mutate({ to_address: address, amount_usd: amount, chain_id: chainId });
      return;
    }
    mutation.mutate({
      to_address: address,
      amount_usd: amount,
      chain_id: chainId,
      stepup_code: isBrowser ? code : undefined,
      max_source_swap: maxSourceSwap,
    });
  };

  const busy = mutation.isPending || challenge.isPending;
  const primaryLabel = mutation.isPending
    ? t('common.loading')
    : isBrowser && !codeSent
      ? t('withdraw.get_code')
      : isBrowser
        ? t('withdraw.confirm_code')
        : t('withdraw.submit_cta');
  const primaryDisabled =
    busy ||
    !canSubmit ||
    (isBrowser && codeSent && !codeValid);

  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const wrapperClass = embedded
    ? 'px-5 pt-2'
    : 'mx-auto max-w-app px-5 pt-6';

  return (
    <div className={wrapperClass}>
      {embedded ? null : (
        <>
          <h1 className="pb-display text-[28px] leading-tight">
            {t('withdraw.title')}
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
            {t('withdraw.subtitle_v2')}
          </p>
        </>
      )}

      <Card pad="md" className="mt-5 space-y-3">
        <div>
          <div className="pb-eyebrow mb-1.5">{t('withdraw.from_label')}</div>
          <div className="flex items-center gap-2 rounded-pb-md border border-pb-border-soft bg-pb-paper2 px-3 py-2.5 text-[14px] font-medium text-tg-text">
            <span className="inline-block h-2 w-2 rounded-full bg-pb-accent" />
            {t('transfer.endpoint_main')}
          </div>
        </div>

        {pusdEligible ? (
          <div>
            <div className="pb-eyebrow mb-1.5">{t('withdraw.token_label')}</div>
            <TokenToggle value={effectiveToken} onChange={setToken} />
          </div>
        ) : null}

        <div className="flex items-baseline justify-between rounded-pb-sm bg-pb-paper2 px-3 py-2">
          <span className="pb-caption">{t('withdraw.available')}</span>
          <span className="pb-numeral text-[16px] text-tg-text">
            <AssetBadge symbol={effectiveToken} size={14} />{' '}
            {available.toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            <span className="text-tg-hint">{effectiveToken}</span>
          </span>
        </div>
        {effectiveToken === 'pUSD' ? (
          <p className="pb-caption">{t('withdraw.pusd_hint')}</p>
        ) : null}
      </Card>

      <Card pad="md" className="mt-4 space-y-4">
        <div>
          <div className="pb-eyebrow mb-1.5">{t('withdraw.to_label')}</div>
          <NetworkSelect value={chain} onChange={setChain} />
        </div>

        <TextField
          label={t('withdraw.address_label_eoa_v2')}
          placeholder="0x…"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value.trim());
            resetSentCode();
          }}
          mono
          spellCheck={false}
          autoComplete="off"
          inputMode="text"
          helper={t('withdraw.address_helper_eoa', {
            chain:
              chain === 'polygon'
                ? t('balances.polygon')
                : t('balances.arbitrum'),
          })}
          error={address && !addrValid ? t('withdraw.err_address') : undefined}
        />

        <TextField
          label={t('withdraw.amount_label')}
          placeholder="0.00"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            resetSentCode();
          }}
          inputMode="decimal"
          type="number"
          trailing={
            <button
              type="button"
              onClick={() => {
                setAmount(available.toFixed(2));
                resetSentCode();
              }}
              className="pb-focus-ring mr-1 rounded-pb-sm px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-pb-accent hover:bg-pb-accent-soft"
            >
              {t('withdraw.max')}
            </button>
          }
          helper={t('withdraw.amount_helper')}
        />

        {error ? (
          <p className="rounded-pb-md bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] px-3 py-2 text-[13px] text-pb-danger">
            {error}
          </p>
        ) : null}

        {isBrowser && codeSent ? (
          <div className="space-y-2">
            <p className="rounded-pb-md border border-pb-border-soft bg-pb-paper2 px-3 py-2 text-[13px] text-tg-hint">
              {t('withdraw.code_sent_hint')}
            </p>
            <TextField
              label={t('withdraw.code_label')}
              placeholder="000000"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              mono
            />
            <button
              type="button"
              onClick={() =>
                challenge.mutate({
                  to_address: address,
                  amount_usd: amount,
                  chain_id: chainId,
                })
              }
              disabled={challenge.isPending}
              className="pb-focus-ring text-[12px] font-medium text-pb-accent hover:underline disabled:opacity-50"
            >
              {t('withdraw.resend_code')}
            </button>
          </div>
        ) : null}

        <Button
          fullWidth
          onClick={submit}
          disabled={primaryDisabled}
          leading={<SendIcon size={16} />}
        >
          {primaryLabel}
        </Button>

        <p className="pb-caption text-center">{t('withdraw.eta')}</p>
      </Card>

      <WithdrawHistorySection
        items={history.data?.items ?? []}
        locale={locale}
      />
    </div>
  );
}

function NetworkSelect({
  value,
  onChange,
}: {
  value: Chain;
  onChange: (c: Chain) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="pb-eyebrow mb-1.5">{t('withdraw.network_label')}</div>
      <div
        className={clsx(
          'grid gap-2',
          VISIBLE_CHAINS.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
        )}
      >
        {VISIBLE_CHAINS.map((c) => {
          const chainId = c === 'polygon' ? POLYGON_CHAIN_ID : ARBITRUM_CHAIN_ID;
          const label =
            c === 'polygon' ? t('balances.polygon') : t('balances.arbitrum');
          const active = c === value;
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={clsx(
                'pb-focus-ring inline-flex items-center justify-center gap-2 rounded-pb-md border px-3 py-2.5 text-[13px] font-medium transition',
                active
                  ? 'border-pb-accent bg-pb-accent-soft text-pb-accent'
                  : 'border-pb-border-soft bg-pb-paper2 text-tg-text hover:bg-pb-border-soft',
              )}
            >
              <NetworkBadge chainId={chainId} size={28} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TokenToggle({
  value,
  onChange,
}: {
  value: 'USDC' | 'pUSD';
  onChange: (v: 'USDC' | 'pUSD') => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {(['USDC', 'pUSD'] as const).map((sym) => {
        const active = sym === value;
        return (
          <button
            key={sym}
            type="button"
            onClick={() => onChange(sym)}
            className={clsx(
              'pb-focus-ring inline-flex items-center justify-center gap-2 rounded-pb-md border px-3 py-2.5 text-[13px] font-medium transition',
              active
                ? 'border-pb-accent bg-pb-accent-soft text-pb-accent'
                : 'border-pb-border-soft bg-pb-paper2 text-tg-text hover:bg-pb-border-soft',
            )}
          >
            <AssetBadge symbol={sym} size={18} />
            {sym}
          </button>
        );
      })}
    </div>
  );
}

type Balances = ReturnType<typeof useMyBalances>['data'];

function chainBalance(balances: Balances, chain: Chain): BalancesChainBalance | null {
  if (!balances) return null;
  return chain === 'polygon'
    ? balances.main_wallet.polygon
    : balances.main_wallet.arbitrum;
}

function tokenBalanceOnChain(balances: Balances, chain: Chain, symbol: string): number {
  const c = chainBalance(balances, chain);
  if (!c) return 0;
  return Number(c.tokens.find((t) => t.symbol === symbol)?.amount ?? 0);
}

/** Max amount the `source='eoa'` withdrawal worker can deliver as
 *  native USDC on `targetChain`, *and* that the user-lane API will
 *  accept at submit: the chain's native USDC paid out directly, plus
 *  the shortfall closed by one pre-swap of the best non-canonical
 *  stable on the same chain ({USDC.e, USDT}), haircut by the 1%
 *  pre-swap buffer. The worker can also fall back to a CCTP burn of
 *  the other chain's native USDC, but `_read_eoa_withdraw_pool` only
 *  counts the selected chain's stables, so a cross-chain row gets
 *  rejected with "insufficient main-wallet balance" — we don't promise
 *  it. Mirrors `withdrawal_processor.eoa_aggregator` ∩ the API's
 *  affordability check. */
export function maxWithdrawableUsdc(balances: Balances, targetChain: Chain): number {
  const usdcNative = tokenBalanceOnChain(balances, targetChain, 'USDC');
  const bestSameChainSwapSource = Math.max(
    tokenBalanceOnChain(balances, targetChain, 'USDC.e'),
    tokenBalanceOnChain(balances, targetChain, 'USDT'),
  );
  return usdcNative + maxAfterPreSwapBuffer(bestSameChainSwapSource);
}

function WithdrawHistorySection({
  items,
  locale,
}: {
  items: WithdrawalItem[];
  locale: string;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [period, setPeriod] = useState<PeriodFilter>('all');

  const filtered = filterByStatusAndPeriod(
    items,
    status,
    period,
    (s) => s === 'completed',
    (s) => s === 'failed' || s === 'rejected',
  );

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="pb-eyebrow">{t('withdraw.history')}</h2>
      </div>
      <div className="mb-3">
        <HistoryFilters
          status={status}
          period={period}
          statusOptions={[
            { value: 'all', label: t('history.filter.all') },
            { value: 'pending', label: t('history.filter.pending') },
            { value: 'done', label: t('history.filter.done') },
            { value: 'failed', label: t('history.filter.failed') },
          ]}
          periodOptions={[
            { value: 'all', label: t('history.filter.all_time') },
            { value: '7d', label: t('history.filter.last_7d') },
            { value: '30d', label: t('history.filter.last_30d') },
          ]}
          onStatusChange={setStatus}
          onPeriodChange={setPeriod}
        />
      </div>
      {filtered.length > 0 ? (
        <Card pad="none" variant="flat">
          <ul className="divide-y divide-pb-border-soft">
            {filtered.map((w) => (
              <li key={w.id}>
                <TxHistoryRow
                  kind="withdrawal"
                  chainId={w.chain_id}
                  asset={w.asset}
                  amountUsd={w.amount_usd}
                  creditedUsd={null}
                  status={withdrawalToStatus(w.status)}
                  createdAt={w.created_at}
                  toAddress={w.to_address}
                  rejectionReason={w.rejection_reason}
                  locale={locale}
                />
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card pad="md" className="text-center">
          <p className="pb-caption">
            {items.length === 0
              ? t('withdraw.no_history')
              : t('history.no_match')}
          </p>
        </Card>
      )}
    </section>
  );
}

function withdrawalToStatus(s: WithdrawalStatus): TxStatus {
  if (s === 'completed') return 'done';
  if (s === 'failed') return 'failed';
  if (s === 'rejected') return 'rejected';
  return 'pending';
}
